import { useState, useMemo } from 'react'
import { useAccount, useReadContracts } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { AmountSlider, Button, FilterPill, Input, MaxLink, Panel, SectionLabel, Stat, ToggleButton } from '@/components/ui'
import { IS_LIVE } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import safetyModuleAbi from '@/lib/abis/SafetyModule.json'
import { guardianPools } from '@/lib/guardianPools'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { fmt, fmtBalance, fmtBalanceWei, fmtUsd, fmtPct, toNumber, validateAmount, weiToInputString, POLL_SLOW, fmtBps } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useProtocolState } from '@/hooks/useProtocolState'
import { EmptyState } from '@/components/dashboard/EmptyState'
import { useLiveAccrual } from '@/hooks/useLiveAccrual'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { useSafetyModuleExtraApy } from '@/hooks/useSafetyModuleExtraApy'
import { coldStartSupplyAprBps, EST_REFERENCE_USD } from '@/lib/coldStart'

const YEAR_SECONDS = 365 * 24 * 60 * 60

// The cold-start estimate moved to lib/coldStart (one implementation for Earn, Markets and
// Vaults), and gained the fix this file's local version needed: it now estimates from the
// UNGOVERNED budget bounded by the on-chain APR cap. The local version read the effective rate,
// which the APR governor (entry 180) zeroes on an empty farm - so the estimate it was built to
// provide had silently stopped firing.
const estHorizonAprBps = coldStartSupplyAprBps

/// The Guardian Pools page - one SafetyModule per reserve (CHANGELOG.md entry 165), laid out the
/// way the pages people actually deposit through are (Aave's Safety Module, Lido, Pendle):
///
///   1. COMPARE FIRST - the entry view is a card per pool showing the numbers a depositor picks
///      by (APY, pool TVL, their own stake), so choosing takes one glance instead of five clicks.
///      The old pill row showed only token names.
///   2. ACTION BESIDE THE STORY - on desktop the detail is two columns: the explanation, stats
///      and steps on the left, and a compact sticky stake/unstake card on the right. A returning
///      staker transacts without scrolling past prose; a first-timer reads the left side. On
///      mobile it stacks in reading order: story and risk first, then the action.
///   3. APY IS THE LOUDEST NUMBER - it heads the action card, with the breakdown as its caption.
///      Education folds away: the four steps sit behind a toggle, closed by default, while the
///      risk box stays permanently visible - the one thing that must never be behind a click.
///
/// Every amount goes through the reserve's own `decimals` and every USD figure multiplies by the
/// reserve's own `priceUsd` (the entry-85 bug class, twice avoided). The seniority story stands
/// because it is true by construction: guardian stakers absorb a shortfall first - the junior
/// tranche - and the yield is the price the protocol pays for that seat.
export function EarnSection() {
  const { address: user } = useAccount()
  const { d, deployed, refetch } = useProtocolState()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const pools = useMemo(() => guardianPools(d.reserves), [d.reserves])
  // USDx is the default selection while it exists - the launch flagship - falling back to
  // whichever pool is first. `selectedKey` survives the pools list refreshing between polls.
  const [selectedKey, setSelectedKey] = useState(null)
  const selected = pools.find((p) => p.reserve.key === selectedKey)
    ?? pools.find((p) => p.reserve.key === 'usdx')
    ?? pools[0]

  const r = selected?.reserve

  // Yield accrues in place by continuously appreciating the sToken exchange rate, so the live
  // number to watch is the USD value of the deposit ticking up. Deliberately `supplyRateBps`
  // (pure interest), not `totalSupplyAprBps` (interest + HORIZON) - the HORIZON incentive is a
  // genuinely separate claim tracked below and folding it in would overstate what a withdrawal
  // actually returns.
  const userValue = r ? r.user.supplyBalance * r.priceUsd : 0
  const earnRatePerSecond = userValue > 0 ? userValue * (r.supplyRateBps / 10_000) / YEAR_SECONDS : 0
  const liveUserValue = useLiveAccrual(userValue, earnRatePerSecond)
  const userSupplyHorizonRatePerSecond = r && r.totalSupplied > 0
    ? toNumber(r.supplyHorizonPerSecond) * (r.user.supplyBalance / r.totalSupplied)
    : 0
  const liveSupplyHorizon = useLiveAccrual(r ? r.user.pendingSupplyHorizon : 0, userSupplyHorizonRatePerSecond)

  // The per-day/week/month/year projections count BOTH streams, unlike the headline value above.
  // The headline is withdrawal truth and stays interest-only; a projection is earnings truth, and
  // interest-only rendered "$0.00" four times over a position accruing thousands in HORIZON
  // (interest rates round to nothing at launch utilisation, emissions dominate). HORIZON enters at
  // today's price, and only when a price exists to enter at.
  const horizonUsdRatePerSecond = hasHorizonPrice ? userSupplyHorizonRatePerSecond * horizonUsd : 0
  const projectedRatePerSecond = earnRatePerSecond + horizonUsdRatePerSecond

  const positionSlot = user && r && r.user.supplyBalance > 0 ? (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Your {r.symbol} Position</SectionLabel>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span className="mono" style={{ color: T.positive, fontFamily: T.fontN, fontSize: 32, fontWeight: 700 }}>
          ${fmtBalance(liveUserValue, 6)}
        </span>
        <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          {fmtBalance(r.user.supplyBalance)} {r.symbol} supplied · earning at the current rate
        </span>
      </div>

      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
        <Stat size="sm" label="Per day" value={fmtUsd(projectedRatePerSecond * 86400)} color={T.positive} />
        <Stat size="sm" label="Per week" value={fmtUsd(projectedRatePerSecond * 86400 * 7)} color={T.positive} />
        <Stat size="sm" label="Per month" value={fmtUsd(projectedRatePerSecond * YEAR_SECONDS / 12)} color={T.positive} />
        <Stat size="sm" label="Per year" value={fmtUsd(projectedRatePerSecond * YEAR_SECONDS)} color={T.positive} />
      </div>
      {horizonUsdRatePerSecond > 0 && (
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          Interest plus HORIZON at today's price.
        </div>
      )}

      {r.supplyHorizonAprBps > 0 && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: T.positive, fontFamily: T.fontN, fontSize: 20, fontWeight: 700 }}>
              <span className="tabnum">{fmtBalance(liveSupplyHorizon, 4)}</span> HORIZON
            </span>
            <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
              pending{hasHorizonPrice ? ` · ≈ ${fmtUsd(liveSupplyHorizon * horizonUsd)}` : ''} · claim on Portfolio
            </span>
          </div>
        </div>
      )}
    </Panel>
  ) : r ? (
    <EstimatePanel r={r} horizonUsd={horizonUsd} hasHorizonPrice={hasHorizonPrice} />
  ) : null

  return (
    <div id="earn">
      <SectionHeader
        title="Protect the Protocol. Earn Yield."
        subtitle="Stake into a Guardian Pool and earn three ways: lending interest, liquidation spreads the pool holds first claim on, and HORIZON rewards. Stakers absorb losses first when a liquidation falls short; the yield is the pay for that seat."
      />

      {(!deployed || pools.length === 0) ? (
        // Not deployed on the connected network (pre-launch, or a wrong-network wallet): one calm
        // not-live panel instead of Guardian Pool cards reading 0.00% APY / $0.00 TVL. Pools appear
        // as soon as the money market is live on this network.
        <EmptyState
          title="Guardian Pools are not live on this network yet."
          body="Point your wallet at a network where Horizon is deployed. Each reserve's Guardian Pool, its yield, and your stake appear here as soon as it is." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* ── Compare first: one card per pool with the numbers a depositor picks by. A single
               pool renders as a single, already-active card - the label doubles as the answer to
               "which asset is this page about". ── */}
          <div style={cardsGrid}>
            {pools.map((p) => (
              <PoolCard
                key={p.reserve.key} pool={p} user={user}
                active={r.key === p.reserve.key}
                onSelect={() => setSelectedKey(p.reserve.key)}
                estAprBps={estHorizonAprBps(p.reserve, horizonUsd, hasHorizonPrice)}
              />
            ))}
          </div>

          {/* `key={r.key}` resets amount/mode state on pool switch, so a half-typed WBTC amount
               never carries into the USDC pool. */}
          <GuardianPoolDetail
            key={r.key} reserve={r} module={selected.module} user={user} onTx={refetch}
            positionSlot={positionSlot}
            estAprBps={estHorizonAprBps(r, horizonUsd, hasHorizonPrice)}
          />
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Pool card: the compare-first entry point. Reads its own module's TVL and the visitor's stake -
// three small reads per card, five cards, all poll-slow.
// ─────────────────────────────────────────────────────────────────

/// The what-if calculator, shown exactly where the position projections would sit for someone who
/// has no position yet (founder request: the projections should exist BEFORE the deposit, on a
/// number the visitor types). Same composition as the live projections - interest plus the supply
/// HORIZON stream at today's price - and the reward share is computed on the pool as it stands
/// after the typed deposit joins, so the estimate is what that deposit would actually capture
/// rather than a rate someone else is currently earning.
function EstimatePanel({ r, horizonUsd, hasHorizonPrice }) {
  const [amount, setAmount] = useState('')
  const amt = Number(amount) > 0 ? Number(amount) : 0
  const amtUsd = amt * r.priceUsd
  const interestPerSecondUsd = amtUsd * (r.supplyRateBps / 10_000) / YEAR_SECONDS
  const share = r.totalSupplied + amt > 0 ? amt / (r.totalSupplied + amt) : 0
  const horizonPerSecondUsd = hasHorizonPrice ? toNumber(r.supplyHorizonPerSecond) * share * horizonUsd : 0
  const ratePerSecondUsd = interestPerSecondUsd + horizonPerSecondUsd

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Estimate Your Earnings</SectionLabel>
      <div style={{ marginTop: 12, maxWidth: 320 }}>
        <Input
          value={amount}
          onChange={setAmount}
          placeholder="0.00"
          suffix={r.symbol}
        />
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
        <Stat size="sm" label="Per day" value={fmtUsd(ratePerSecondUsd * 86400)} color={T.positive} />
        <Stat size="sm" label="Per week" value={fmtUsd(ratePerSecondUsd * 86400 * 7)} color={T.positive} />
        <Stat size="sm" label="Per month" value={fmtUsd(ratePerSecondUsd * YEAR_SECONDS / 12)} color={T.positive} />
        <Stat size="sm" label="Per year" value={fmtUsd(ratePerSecondUsd * YEAR_SECONDS)} color={T.positive} />
      </div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
        Interest plus HORIZON at today's price, on the pool as it stands after your deposit.
      </div>
    </Panel>
  )
}

function PoolCard({ pool, user, active, onSelect, estAprBps }) {
  const { reserve: r, module } = pool
  const { data } = useReadContracts({
    contracts: [
      { address: module, abi: safetyModuleAbi, functionName: 'totalAssets' },
      { address: module, abi: safetyModuleAbi, functionName: 'totalShares' },
      { address: module, abi: safetyModuleAbi, functionName: 'sharesOf', args: user ? [user] : undefined },
    ],
    query: { enabled: IS_LIVE, refetchInterval: POLL_SLOW },
  })
  const totalAssets = toNumber(data?.[0]?.result ?? 0n, r.decimals)
  const totalShares = toNumber(data?.[1]?.result ?? 0n, r.decimals)
  const userShares = user ? data?.[2]?.result ?? 0n : 0n
  const userStake = totalShares > 0 ? (toNumber(userShares, r.decimals) / totalShares) * totalAssets : 0

  return (
    <button
      onClick={onSelect} aria-pressed={active}
      style={{
        ...poolCard,
        border: `1px solid ${active ? T.accent : T.border}`,
        background: active ? T.accentDim : 'rgba(255,255,255,0.02)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <TokenGlyph symbol={r.symbol} size={40} />
        <span style={{ color: T.text, fontFamily: T.fontD, fontSize: T.fs.ui, fontWeight: 700 }}>{r.symbol}</span>
      </div>
      <div className="mono" style={{ marginTop: 10, color: T.positive, fontFamily: T.fontN, fontSize: 26, fontWeight: 700 }}>
        {estAprBps > 0 ? `≈ ${fmtBps(estAprBps)}` : fmtBps(r.totalSupplyAprBps)}
        <span style={{ fontSize: T.fs.caption, fontWeight: 400, color: T.textDim, marginLeft: 6 }}>
          {estAprBps > 0 ? 'est. APY' : 'APY'}
        </span>
      </div>
      {estAprBps > 0 && (
        <div style={{ marginTop: 4, color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          HORIZON rewards for the first stakers
        </div>
      )}
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
        Pool TVL {fmtUsd(totalAssets * r.priceUsd)}
        {/* Coverage: how much of this reserve's outstanding borrows the pool could absorb - the
            backstop's honest capacity figure (entry 181; the changelog's open table has wanted
            this published since the Guardian Pool research). Same asset on both sides, so the
            ratio is priceless arithmetic. Shown once the reserve has real debt. */}
        {r.totalBorrows > 0 && (
          <span> · covers {fmtPct(Math.min((totalAssets / r.totalBorrows) * 100, 999))} of borrows</span>
        )}
      </div>
      {user && userStake > 0 && (
        <div style={{ marginTop: 4, color: T.positive, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          Your stake {fmtUsd(userStake * r.priceUsd)}
        </div>
      )}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────
// One Guardian Pool's detail, fully parameterized by its reserve: addresses, DECIMALS and price
// all come from the reserve entry. Owns every module read and both transactions; renders the
// two-column desktop layout (story left, sticky action card right) and the stacked mobile one.
// ─────────────────────────────────────────────────────────────────

function GuardianPoolDetail({ reserve, module, user, onTx, positionSlot, estAprBps }) {
  const action = useAction()
  const isWide = useMediaQuery('(min-width: 1024px)')
  const [mode, setMode] = useState('stake') // 'stake' | 'unstake'
  const [amount, setAmount] = useState('')
  const err = amount ? validateAmount(amount) : ''
  const dec = reserve.decimals
  const priceUsd = reserve.priceUsd
  const sym = reserve.symbol
  const underlying = reserve.key === 'eth' ? 'WETH' : sym
  const sSym = `s${reserve.key === 'eth' ? 'ETH' : sym}`
  const wei = useMemo(() => { try { return amount && !err ? parseUnits(amount, dec) : 0n } catch { return 0n } }, [amount, err, dec])

  // Global figures (TVL, cooldown params) are readable by anyone, connected or not - split from
  // the user-specific reads so an anonymous visitor still sees a real TVL instead of nothing.
  const { data: globalReads } = useReadContracts({
    contracts: [
      { address: module, abi: safetyModuleAbi, functionName: 'totalAssets' },
      { address: module, abi: safetyModuleAbi, functionName: 'totalShares' },
      { address: module, abi: safetyModuleAbi, functionName: 'cooldownDuration' },
      { address: module, abi: safetyModuleAbi, functionName: 'withdrawWindow' },
    ],
    query: { enabled: IS_LIVE, refetchInterval: POLL_SLOW },
  })
  const { data: userReads, refetch: refetchUserReads } = useReadContracts({
    contracts: [
      { address: module, abi: safetyModuleAbi, functionName: 'sharesOf', args: user ? [user] : undefined },
      { address: module, abi: safetyModuleAbi, functionName: 'cooldowns', args: user ? [user] : undefined },
      { address: reserve.sTokenAddress, abi: ERC20_ABI, functionName: 'balanceOf', args: user ? [user] : undefined },
      // The plain underlying too, for the one-transaction zap - a staker who has never supplied
      // holds no sToken at all, and sending them to another page to get some is exactly the
      // friction stakeUnderlying exists to remove. (Allowances are no longer read here - useAction
      // folds any needed approval into the stake as a pull.)
      { address: reserve.asset, abi: ERC20_ABI, functionName: 'balanceOf', args: user ? [user] : undefined },
    ],
    query: { enabled: IS_LIVE && !!user, refetchInterval: POLL_SLOW },
  })
  const totalAssetsWei = globalReads?.[0]?.result ?? 0n
  const totalSharesWei = globalReads?.[1]?.result ?? 0n
  // Everything below is in the DEFENDED ASSET's units, at ITS decimals - the hardcoded 18 this
  // page used to carry was the entry-85 bug class lying in wait for the USDC and WBTC pools.
  const totalAssets = toNumber(totalAssetsWei, dec)
  const totalShares = toNumber(totalSharesWei, dec)
  const cooldownDuration = Number(globalReads?.[2]?.result ?? 0n)
  const withdrawWindow = Number(globalReads?.[3]?.result ?? 0n)
  const userShares = userReads?.[0]?.result ?? 0n
  const cooldown = userReads?.[1]?.result // [shares, unlockAt] or undefined
  const sTokenBal = userReads?.[2]?.result ?? 0n
  const underlyingBal = userReads?.[3]?.result ?? 0n
  const refetchReads = refetchUserReads

  const userRedeemable = totalShares > 0 ? (toNumber(userShares, dec) / totalShares) * totalAssets : 0
  // Exact redeemable value, in wei, straight from the same shares/assets ratio the contract uses.
  // Both the slider's MAX position and the MaxLink read this one value, so they stay identical by
  // construction. Bigint division floors, so MAX can never round UP past the real balance and
  // revert on submit.
  const unstakeMaxWei = useMemo(
    () => (totalSharesWei > 0n ? (userShares * totalAssetsWei) / totalSharesWei : 0n),
    [userShares, totalAssetsWei, totalSharesWei],
  )
  const { extraAprBps, netProfit, absorptionCount } = useSafetyModuleExtraApy(module, dec, totalAssets)
  const cooldownShares = cooldown?.[0] ?? 0n
  const unlockAt = Number(cooldown?.[1] ?? 0n)
  const hasCooldown = cooldownShares > 0n
  const nowSec = Math.floor(Date.now() / 1000)
  const cooldownReady = hasCooldown && nowSec >= unlockAt
  const withdrawExpired = hasCooldown && nowSec > unlockAt + withdrawWindow
  const cooldownRedeemable = totalShares > 0 ? (toNumber(cooldownShares, dec) / totalShares) * totalAssets : 0

  const isStake = mode === 'stake'
  // Which token the staker is paying with. The underlying routes through `stakeUnderlying`, which
  // supplies to the pool and stakes the resulting sToken in one transaction; the sToken routes
  // through plain `stake` for anyone who already holds it. Defaults to the underlying because that
  // is what a new staker actually has.
  const [payWith, setPayWith] = useState('underlying') // 'underlying' | 'stoken'
  const payUnderlying = payWith === 'underlying'
  const payBal = payUnderlying ? underlyingBal : sTokenBal
  const paySymbol = payUnderlying ? underlying : sSym
  const payToken = payUnderlying ? reserve.asset : reserve.sTokenAddress

  const overBalance = isStake && !!user && wei > 0n && wei > payBal
  const sharesToUnstake = !isStake && totalAssets > 0
    ? BigInt(Math.floor((toNumber(wei, dec) / totalAssets) * toNumber(totalSharesWei, 0)))
    : 0n
  const overStaked = !isStake && !!user && wei > 0n && sharesToUnstake > userShares
  // Approval (payToken -> module) is folded into submit() by useAction as a pull; no separate step.
  const canSubmit = !!user && wei > 0n && !overBalance && !overStaked

  async function submit() {
    let res
    if (isStake) {
      // Both stakeUnderlying and stake pull payToken into the module, so the intent carries that pull.
      res = await action.run({
        pulls: [{ token: payToken, spender: module, amount: wei }],
        calls: [{ address: module, abi: safetyModuleAbi, functionName: payUnderlying ? 'stakeUnderlying' : 'stake', args: [wei] }],
        permit: { token: payToken, amount: wei, spender: module,
          buildCall: (p, sig) => ({ address: module, abi: safetyModuleAbi, functionName: payUnderlying ? 'stakeUnderlyingWithPermit' : 'stakeWithPermit', args: [p, sig] }) },
        successMsg: `Staked ${amount} ${paySymbol} into the ${sym} Guardian Pool.`,
      })
    } else {
      res = await action.run({ calls: [{ address: module, abi: safetyModuleAbi, functionName: 'requestUnstake', args: [sharesToUnstake] }], successMsg: `Unstake requested - available to withdraw in ${fmtDuration(cooldownDuration)}.` })
    }
    if (!res) return
    setAmount('')
    refetchReads().catch((e) => console.error('refetchReads after tx failed:', e))
    onTx?.().catch?.((e) => console.error('onTx refetch after guardian tx failed:', e))
  }

  async function withdraw() {
    const res = await action.run({ calls: [{ address: module, abi: safetyModuleAbi, functionName: 'withdraw', args: [] }], successMsg: `Withdrawn from the ${sym} Guardian Pool.` })
    if (!res) return
    refetchReads().catch((e) => console.error('refetchReads after withdraw failed:', e))
    onTx?.().catch?.((e) => console.error('onTx refetch after withdraw failed:', e))
  }

  // ── Left column: the story, the risk, the numbers. ──
  const infoCard = (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>{sym} Guardian Pool</SectionLabel>
      <div style={{ marginTop: 10, color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>
        Earn more {underlying} by protecting the {sym} market.
      </div>
      <div style={{ marginTop: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
        Your {underlying} keeps earning lending interest the whole time it is staked. When a risky
        loan needs clearing and other liquidators pass on it, this pool steps in, takes the
        collateral at a discount, and the profit grows every staker's balance.
      </div>
      {/* States the downside at the same visual weight as the upside, and stays permanently
          visible - the one thing on this page that must never fold away. Stakers really are the
          party that absorbs a shortfall; that is the seat the yield pays for. */}
      <div style={{
        marginTop: 12, padding: '12px 14px', borderRadius: T.r.md,
        border: `1px solid ${T.warning}44`, background: `${T.warning}11`,
        color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6,
      }}>
        <strong style={{ color: T.warning }}>Risk:</strong> This pool covers the {sym} market's
        losses. When clearing a loan brings back less than it cost, the difference comes out of the
        pool, and you may withdraw less {underlying} than you put in. Unstaking takes a
        {' '}{fmtDuration(cooldownDuration)} cooldown, so exits settle after losses land.
      </div>

      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Stat size="sm" label="Guardian Pool TVL" value={fmtUsd(totalAssets * priceUsd)} sub={`${fmt(totalAssets, 2)} ${sym}`} />
        <Stat size="sm" label={`${sym} market size`} value={fmtUsd(reserve.totalSupplied * priceUsd)} sub={`${fmtBps(reserve.utilizationBps)} utilization`} />
        {absorptionCount > 0 && extraAprBps !== null && (
          <Stat
            size="sm" label="Liquidation bonus"
            value={`${extraAprBps >= 0 ? '+' : ''}${fmtBps(extraAprBps)} APY`}
            color={extraAprBps >= 0 ? T.positive : T.negative}
            sub="Extra yield from liquidations this pool has won."
          />
        )}
        {/* The rate and the total answer different questions - how fast, versus how much - and the
            total is the one that survives a quiet month. Same event scan, no extra read. */}
        {absorptionCount > 0 && netProfit !== null && (
          <Stat
            size="sm" label="Earned from liquidations"
            value={fmtUsd(netProfit * priceUsd)}
            color={netProfit >= 0 ? T.positive : T.negative}
            sub="since launch"
          />
        )}
        {user && userShares > 0n && <Stat size="sm" label="Your position" value={fmtUsd(userRedeemable * priceUsd)} color={T.positive} />}
      </div>
    </Panel>
  )

  // ── Right column: the action card. APY leads - the loudest number on the page - then the form.
  //     Sticky on desktop so it rides along while the left column scrolls. ──
  const actionCard = (
    <Panel style={{ padding: 28 }}>
      <div style={inputLabel}>{sym} Guardian APY</div>
      <div className="mono" style={{ marginTop: 6, color: T.positive, fontFamily: T.fontN, fontSize: 40, fontWeight: 800, lineHeight: 1 }}>
        {estAprBps > 0 ? `≈ ${fmtBps(estAprBps)}` : fmtBps(reserve.totalSupplyAprBps)}
      </div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
        {estAprBps > 0
          ? `Estimated from HORIZON rewards at today's emission rate, on the first ${fmtUsd(EST_REFERENCE_USD, 0)} staked. This pool is open and paying: early stakers split the whole stream between them.`
          : <>
              {reserve.supplyHorizonAprBps > 0
                ? `${fmtBps(reserve.supplyRateBps)} interest + ${fmtBps(reserve.supplyHorizonAprBps)} HORIZON`
                : `${fmtBps(reserve.supplyRateBps)} lending interest`}
              {absorptionCount > 0 && extraAprBps !== null && extraAprBps > 0 &&
                ` + ${fmtBps(extraAprBps)} realized from liquidations`}
            </>}
      </div>

      {hasCooldown && (
        <div style={{
          marginTop: 18, padding: 16, borderRadius: T.r.md,
          border: `1px solid ${withdrawExpired ? T.negative + '55' : T.border}`,
          background: withdrawExpired ? 'rgba(248, 113, 113, 0.08)' : 'rgba(255,255,255,0.02)',
        }}>
          <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700, marginBottom: 6 }}>
            Pending unstake: ≈ {fmtUsd(cooldownRedeemable * priceUsd)}
          </div>
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, marginBottom: 12 }}>
            {withdrawExpired
              ? 'The withdraw window has closed. Request unstake again to restart the cooldown.'
              : cooldownReady
                ? `Ready to withdraw - available for another ${fmtDuration(unlockAt + withdrawWindow - nowSec)}.`
                : `Available to withdraw in ${fmtDuration(unlockAt - nowSec)}.`}
          </div>
          <Button onClick={withdraw} disabled={!cooldownReady || withdrawExpired || action.pending}
            style={{ padding: '10px 18px', fontSize: T.fs.caption }}>
            {action.pending ? 'Working…' : 'Withdraw'}
          </Button>
        </div>
      )}

      <div style={{ marginTop: 18, ...modeRow }}>
        {['stake', 'unstake'].map((m) => (
          <ToggleButton
            key={m} size="lg" active={mode === m}
            onClick={() => { setMode(m); setAmount('') }}
            style={{ textTransform: 'capitalize' }}
          >{m}</ToggleButton>
        ))}
      </div>

      {/* Pay-with selector. Only meaningful while staking - unstaking always redeems to the
          sToken, so showing this on that tab would imply a choice that does not exist. */}
      {isStake && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>Deposit</span>
          {[['underlying', underlying], ['stoken', sSym]].map(([id, label]) => (
            <FilterPill
              key={id} size="sm" active={payWith === id}
              onClick={() => { setPayWith(id); setAmount('') }}
              style={{ padding: '6px 12px', background: payWith === id ? T.accentDim : 'transparent' }}
            >{label}</FilterPill>
          ))}
          <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            {payUnderlying ? 'one step: supplied and staked together' : 'stake the deposit you already hold'}
          </span>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={inputLabel}>{isStake ? `${paySymbol} amount` : `${sym}-equivalent amount`}</span>
          {err && <span style={{ color: T.negative, fontSize: T.fs.caption, fontFamily: T.fontM }}>{err}</span>}
        </div>
        <input
          type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
          style={inputBox}
        />
        <div style={{ textAlign: 'right', color: T.textDim, fontSize: T.fs.caption, marginTop: 6, fontFamily: T.fontM }}>
          {isStake
            ? `Available: ${user ? fmtBalanceWei(payBal, dec, 2) : '-'} ${paySymbol}`
            : `Staked: ${user ? fmtUsd(userRedeemable * priceUsd, 2) : '-'}`}
          {user && (isStake ? payBal > 0n : userRedeemable > 0) && (
            <MaxLink onClick={() => setAmount(weiToInputString(isStake ? payBal : unstakeMaxWei, dec))} />
          )}
        </div>
        <AmountSlider
          valueWei={wei} maxWei={isStake ? payBal : unstakeMaxWei}
          onChangeWei={(w) => setAmount(weiToInputString(w, dec))} disabled={!user}
        />
        {!isStake && (
          <div style={{ marginTop: 10, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            Unstaking starts a {fmtDuration(cooldownDuration)} cooldown, then a {fmtDuration(withdrawWindow)} window to
            withdraw. Your funds stay in place, earning, until the withdrawal itself.
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        {!user ? (
          <Button disabled style={{ width: '100%', padding: '16px 18px', fontSize: T.fs.ui }}>Connect wallet</Button>
        ) : (
          <Button onClick={submit} disabled={!canSubmit || action.pending} style={{ width: '100%', padding: '16px 18px', fontSize: T.fs.ui }}>
            {action.pending ? 'Working…' : isStake ? `Stake ${amount || '0'} ${paySymbol}` : `Request unstake of ${amount || '0'}`}
          </Button>
        )}
      </div>
    </Panel>
  )

  // Desktop: story left, sticky action right, so a returning staker transacts without scrolling.
  // Mobile: reading order - story and risk first, then the action, then position. The step-by-step
  // mechanism now lives in the page-level "How earning works" panel (ProcessBenefits), so this
  // section no longer carries its own duplicate walkthrough.
  return isWide ? (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 24, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
        {infoCard}
        {positionSlot}
      </div>
      <div style={{ position: 'sticky', top: 24 }}>
        {actionCard}
      </div>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {infoCard}
      {actionCard}
      {positionSlot}
    </div>
  )
}

/// Durations in words - "5 days", "36 hours" - because "5d 0h" reads like a log line, and this
/// number appears inside sentences a first-time staker has to trust. Zero components are dropped;
/// two units at most.
function fmtDuration(seconds) {
  if (seconds <= 0) return 'now'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const unit = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`
  if (days > 0) return hours > 0 ? `${unit(days, 'day')} ${unit(hours, 'hour')}` : unit(days, 'day')
  if (hours > 0) return minutes > 0 ? `${unit(hours, 'hour')} ${unit(minutes, 'minute')}` : unit(hours, 'hour')
  return unit(minutes, 'minute')
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

// Five cards across on a wide screen, wrapping down to two and then one - same auto-fit idiom as
// the stat grids, so the row degrades without a breakpoint of its own.
const cardsGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12,
}
const poolCard = {
  padding: '16px 18px', borderRadius: T.r.lg, cursor: 'pointer', textAlign: 'left',
  font: 'inherit', color: 'inherit', display: 'block', width: '100%',
}

const modeRow = { display: 'flex', gap: 8 }
const inputLabel = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
}
const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '14px 14px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
